/**
 * DynamicUpcomingIcon — Calendar icon with live clock hands.
 *
 * The clock hands rotate to match the current system time,
 * updating every minute. Built from the original calendar-upcoming SVG
 * with the static clock hands extracted and replaced by dynamic ones.
 *
 * Exposes two variants via separate named exports:
 *   - DynamicUpcomingStroke  (outline style)
 *   - DynamicUpcomingFill    (solid/filled style)
 *
 * Both accept the same SVGProps that the ?react SVG imports provide,
 * so they can drop in as replacements in the icon registry.
 */
import { forwardRef, useEffect, useState, type SVGProps } from "react"

// ---------------------------------------------------------------------------
// Shared clock-hand logic
// ---------------------------------------------------------------------------

/** Returns [hourAngle, minuteAngle] in degrees (0 = 12 o'clock). */
function getClockAngles(): [number, number] {
  const now = new Date()
  const h = now.getHours() % 12
  const m = now.getMinutes()
  const hourAngle = h * 30 + m * 0.5 // 360/12 = 30° per hour + 0.5° per minute
  const minuteAngle = m * 6 // 360/60 = 6° per minute
  return [hourAngle, minuteAngle]
}

function useClockAngles() {
  const [angles, setAngles] = useState(getClockAngles)

  useEffect(() => {
    // Sync to the start of the next minute for crisp updates
    const now = new Date()
    const msUntilNextMinute =
      (60 - now.getSeconds()) * 1000 - now.getMilliseconds()

    const timeout = setTimeout(() => {
      setAngles(getClockAngles())
      // Then tick every 60 s
      const interval = setInterval(() => setAngles(getClockAngles()), 60_000)
      // Store so we can clean up
      cleanupRef = interval
    }, msUntilNextMinute)

    let cleanupRef: ReturnType<typeof setInterval> | null = null

    return () => {
      clearTimeout(timeout)
      if (cleanupRef) clearInterval(cleanupRef)
    }
  }, [])

  return angles
}

// ---------------------------------------------------------------------------
// Clock-hand geometry
// ---------------------------------------------------------------------------

// Calendar body (below pin tabs) spans roughly y≈8 to y≈21.5.
// Visual center of that area ≈ 15.5.  CX = 12 for horizontal center.
const STROKE_CX = 12
const STROKE_CY = 14.5
const STROKE_MINUTE_LENGTH = 4.2
const STROKE_HOUR_LENGTH = 2.5

// Solid variant: same center position
const SOLID_CX = 12
const SOLID_CY = 14.5
const SOLID_MINUTE_LENGTH = 4.2
const SOLID_HOUR_LENGTH = 2.5

// ---------------------------------------------------------------------------
// STROKE variant (outline calendar, dynamic hands)
// ---------------------------------------------------------------------------

// Calendar body — original path minus the clock-hand segment
// Original: d="M8.22222 2.5V4.52138 ... M11.96 10.25V14.2392L13.96 15.2392"
// We keep everything up to (and excluding) the last M command.
const STROKE_CALENDAR_D =
  "M8.22222 2.5V4.52138M8.22222 6.3V4.52138M15.7778 2.5V4.52138M15.7778 6.3V4.52138" +
  "M8.22222 4.52138C9.111 4.4 10.2731 4.4 12 4.4C13.7269 4.4 14.889 4.4 15.7778 4.52138" +
  "M8.22222 4.52138C7.75207 4.5856 7.35841 4.68377 6.99813 4.83389C5.60963 5.4124 4.50648 6.52204 3.93135 7.9187C3.69204 8.49986 3.58549 9.16733 3.53806 10.1C3.5 10.8484 3.5 11.7676 3.5 12.95C3.5 15.6059 3.5 16.9338 3.93135 17.9813C4.50648 19.378 5.60963 20.4876 6.99813 21.0661C8.0395 21.5 9.35966 21.5 12 21.5C14.6403 21.5 15.9605 21.5 17.0019 21.0661C18.3904 20.4876 19.4935 19.378 20.0687 17.9813C20.5 16.9338 20.5 15.6059 20.5 12.95C20.5 11.7676 20.5 10.8484 20.4619 10.1C20.4145 9.16733 20.308 8.49986 20.0687 7.9187C19.4935 6.52204 18.3904 5.4124 17.0019 4.83389C16.6416 4.68377 16.2479 4.5856 15.7778 4.52138"

export const DynamicUpcomingStroke = forwardRef<
  SVGSVGElement,
  SVGProps<SVGSVGElement>
>(function DynamicUpcomingStroke(props, ref) {
  const [hourAngle, minuteAngle] = useClockAngles()

  return (
    <svg
      ref={ref}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      {...props}
    >
      {/* Calendar body */}
      <path
        d={STROKE_CALENDAR_D}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Minute hand — rotates from center to "up" position */}
      <line
        x1={STROKE_CX}
        y1={STROKE_CY}
        x2={STROKE_CX}
        y2={STROKE_CY - STROKE_MINUTE_LENGTH}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        transform={`rotate(${minuteAngle}, ${STROKE_CX}, ${STROKE_CY})`}
      />
      {/* Hour hand — shorter, rotates from center */}
      <line
        x1={STROKE_CX}
        y1={STROKE_CY}
        x2={STROKE_CX}
        y2={STROKE_CY - STROKE_HOUR_LENGTH}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        transform={`rotate(${hourAngle}, ${STROKE_CX}, ${STROKE_CY})`}
      />
    </svg>
  )
})

// ---------------------------------------------------------------------------
// SOLID variant (filled calendar, dynamic hands)
// ---------------------------------------------------------------------------

// Calendar body — original path minus the clock-hand ZM...Z portion.
// The original solid path ends with "ZM13.1081 14.1724V10.3634C...Z"
// We keep the first Z and remove everything after.
const SOLID_CALENDAR_D =
  "M7.00334 3.30403C5.28219 3.78348 4.01468 4.92884 3.2942 5.92769C2.48726 7.04641 2.09606 8.22639 2.05337 9.74998C2.05218 9.79243 2.051 9.83451 2.04983 9.87626C2.02232 10.8561 2 11.6509 2 12.6933V14.6644C2 15.1172 2.01334 17.0616 2.33356 18.1404C2.65377 19.2191 3.34757 20.2846 4.29486 21.1636C5.24216 22.0426 6.37625 22.6019 7.4036 22.7751C8.43095 22.9482 9.5517 22.9882 10.459 22.9882C10.7548 22.9882 11.0974 22.991 11.4475 22.9939C12.1712 22.9999 12.927 23.0061 13.3676 22.9882C13.4673 22.9841 13.6082 22.9828 13.7803 22.9813C14.737 22.9726 16.6571 22.9552 17.8105 22.4022C19.1714 21.7496 20.0935 20.9874 20.8793 19.8051C21.7732 18.46 22 17.1282 22 15.1039V10.7089C22 9.00417 21.7465 7.63241 21.1461 6.62023C20.3403 5.26179 19.0647 3.87671 17.01 3.30403V2.01217C17.01 1.39954 16.5163 1 16.0093 1C15.4756 1 15.0087 1.42618 15.0087 1.99886V3.03767C14.1681 3.02435 12.3296 2.99771 11.6998 2.99771C11.1127 2.99771 9.75183 2.99771 9.00467 3.05099V2.03881C9.00467 1.47945 8.59106 1 7.99066 1C7.41694 1 7.00334 1.48478 7.00334 1.98554V3.30403Z"

export const DynamicUpcomingFill = forwardRef<
  SVGSVGElement,
  SVGProps<SVGSVGElement>
>(function DynamicUpcomingFill(props, ref) {
  const [hourAngle, minuteAngle] = useClockAngles()

  // The clock hands sit on top of the filled calendar body, so they need
  // a contrasting color. Use the page background (works for light & dark).
  const handColor = "var(--background)"

  return (
    <svg
      ref={ref}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      {...props}
    >
      {/* Filled calendar body */}
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d={SOLID_CALENDAR_D}
        fill="currentColor"
      />
      {/* Minute hand — contrasting color against filled body */}
      <line
        x1={SOLID_CX}
        y1={SOLID_CY}
        x2={SOLID_CX}
        y2={SOLID_CY - SOLID_MINUTE_LENGTH}
        stroke={handColor}
        strokeWidth="2.2"
        strokeLinecap="round"
        transform={`rotate(${minuteAngle}, ${SOLID_CX}, ${SOLID_CY})`}
      />
      {/* Hour hand — contrasting color against filled body */}
      <line
        x1={SOLID_CX}
        y1={SOLID_CY}
        x2={SOLID_CX}
        y2={SOLID_CY - SOLID_HOUR_LENGTH}
        stroke={handColor}
        strokeWidth="2.2"
        strokeLinecap="round"
        transform={`rotate(${hourAngle}, ${SOLID_CX}, ${SOLID_CY})`}
      />
    </svg>
  )
})
